import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { isGeoJSONSource } from '../../components/map/map-engine.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'

const SOURCE_ID = 'graticule-source'
const LINE_LAYER_ID = 'graticule-lines'
const LABEL_LAYER_ID = 'graticule-labels'
const DEFAULT_OPACITY = 1.0
const DEFAULT_COLOR = '#333333'

function graticuleInterval(zoom: number): number {
  if (zoom < 3) return 30
  if (zoom < 4) return 10
  if (zoom < 6) return 5
  if (zoom < 8) return 1
  if (zoom < 10) return 0.5
  if (zoom < 12) return 0.1
  if (zoom < 14) return 1 / 60 // 1'
  return 0.5 / 60 // 30"
}

function buildGraticule(bounds: maplibregl.LngLatBounds, zoom: number): GeoJSON.FeatureCollection {
  const interval = graticuleInterval(zoom)
  const pad = interval * 2

  const west = Math.floor((bounds.getWest() - pad) / interval) * interval
  const east = Math.ceil((bounds.getEast() + pad) / interval) * interval
  const south = Math.max(-85, Math.floor((bounds.getSouth() - pad) / interval) * interval)
  const north = Math.min(85, Math.ceil((bounds.getNorth() + pad) / interval) * interval)

  const features: GeoJSON.Feature[] = []

  for (let lng = west; lng <= east; lng += interval) {
    const clampedLng = Math.round(lng * 1e8) / 1e8
    features.push({
      type: 'Feature',
      properties: { value: clampedLng, axis: 'lng', label: formatDeg(clampedLng, 'lng') },
      geometry: {
        type: 'LineString',
        coordinates: [
          [clampedLng, south],
          [clampedLng, north],
        ],
      },
    })
  }

  for (let lat = south; lat <= north; lat += interval) {
    const clampedLat = Math.round(lat * 1e8) / 1e8
    features.push({
      type: 'Feature',
      properties: { value: clampedLat, axis: 'lat', label: formatDeg(clampedLat, 'lat') },
      geometry: {
        type: 'LineString',
        coordinates: [
          [west, clampedLat],
          [east, clampedLat],
        ],
      },
    })
  }

  return { type: 'FeatureCollection', features }
}

function formatDeg(value: number, axis: 'lat' | 'lng'): string {
  const abs = Math.abs(value)
  const d = Math.floor(abs)
  const mf = (abs - d) * 60
  const m = Math.floor(mf)
  const s = Math.round((mf - m) * 60)
  const dir = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W'
  if (m === 0 && s === 0) return `${d}°${dir}`
  if (s === 0) return `${d}°${m}'${dir}`
  return `${d}°${m}'${s}"${dir}`
}

class GraticuleService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _color = DEFAULT_COLOR
  private updateHandler = () => this.update()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.map.off('moveend', this.updateHandler)
    this.removeSourceAndLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map || !(this.map as unknown as Record<string, unknown>).style) return
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.setPaintProperty(LINE_LAYER_ID, 'line-opacity', this._opacity)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-opacity', this._opacity)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.removeSourceAndLayers()
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  private update(): void {
    if (!this.map || !this._enabled) return
    const bounds = this.map.getBounds()
    const zoom = this.map.getZoom()
    const data = buildGraticule(bounds, zoom)
    const source = this.map.getSource(SOURCE_ID)
    if (isGeoJSONSource(source)) source.setData(data)
  }

  private addSourceAndLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }

    if (!this.map.getLayer(LINE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: {
            'line-color': this._color,
            'line-opacity': this._opacity,
            'line-width': 2,
            'line-dasharray': [6, 3],
          },
        },
        'graticule'
      )
    }

    if (!this.map.getLayer(LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          layout: {
            'symbol-placement': 'line',
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 12,
            'text-max-angle': 0,
            'text-padding': 20,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': this._color,
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'graticule'
      )
    }
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }

  private removeSourceAndLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(LABEL_LAYER_ID)) this.map.removeLayer(LABEL_LAYER_ID)
    if (this.map.getLayer(LINE_LAYER_ID)) this.map.removeLayer(LINE_LAYER_ID)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const graticuleService = new GraticuleService()
